import { ILinks, TDropdownType } from './interfaces';
import { IDropdownMenuLink } from './dropdown_menu';
import {
  IHeaderCommercialData,
  IHeaderNewbuildingsData,
  IHeaderRentData,
  IHeaderSaleData,
} from '../../app/models/header';
import { mergePaths } from '@cian/http-api/shared';
import { IHeaderV1Schema } from '../../shared/repositories/header/entities/response/HeaderV1Schema';
import {
  BC_LINK_TITLE,
  BUILDER_PROMO_LINK_TITLE,
  COTTAGE_LINK_TITLE,
  DAILY_LINK_TITLE,
  DEAL_RENT_LINK_TITLE,
  DOM_RF_TITLE,
  JK_FLAT_LINK_TITLE,
  JKS_LINK_TITLE,
  KP_LINK_TITLE,
  LONG_TERM_LINK_TITLE,
  SC_LINK_TITLE,
  SECONDARY_LINK_TITLE,
  WC_LINK_TITLE,
} from './const';

export const getInitialCommercialLinks = (commercialData?: IHeaderCommercialData) => {
  let innerCommercialData = commercialData;
  const links: IDropdownMenuLink[] = [];

  if (typeof window !== 'undefined') {
    innerCommercialData = window.__header_commercial_data__;
  }

  if (!innerCommercialData) {
    return links;
  }

  if (innerCommercialData.isBcRegion) {
    links.push({
      url: innerCommercialData.bcUrl,
      text: BC_LINK_TITLE,
      analyticsLabel: 'BC',
    });
  }

  if (innerCommercialData.isScRegion) {
    links.push({
      url: innerCommercialData.scUrl,
      text: SC_LINK_TITLE,
      analyticsLabel: 'TC',
    });
  }

  if (innerCommercialData.isWcRegion) {
    links.push({
      url: innerCommercialData.wcUrl,
      text: WC_LINK_TITLE,
      analyticsLabel: 'WC',
    });
  }

  return links;
};

export const getInitialRentLinks = (rentData?: IHeaderRentData) => {
  let innerRentData = rentData;
  const links: IDropdownMenuLink[] = [];

  if (typeof window !== 'undefined') {
    innerRentData = window.__header_rent_data__;
  }

  if (!innerRentData) {
    return links;
  }

  if (innerRentData.longTermUrl) {
    links.push({
      url: innerRentData.longTermUrl,
      text: LONG_TERM_LINK_TITLE,
      analyticsLabel: 'long_term',
    });
  }

  if (innerRentData.dailyUrl) {
    links.push({
      url: innerRentData.dailyUrl,
      text: DAILY_LINK_TITLE,
      analyticsLabel: 'daily',
    });
  }

  if (innerRentData.isDomrf && innerRentData.domrfUrl) {
    links.push({
      url: innerRentData.domrfUrl,
      text: DOM_RF_TITLE,
      isDomrfLink: true,
      analyticsEvent: 'eventSite',
      analyticsLabel: 'domrf',
    });
  }

  if (innerRentData.isDealRent && innerRentData.dealRentUrl) {
    links.push({
      url: innerRentData.dealRentUrl,
      text: DEAL_RENT_LINK_TITLE,
      analyticsLabel: 'deal',
    });
  }

  return links;
};

export const getInitialSaleLinks = (saleData?: IHeaderSaleData) => {
  let innerSaleData = saleData;
  const links: IDropdownMenuLink[] = [];
  if (typeof window !== 'undefined') {
    innerSaleData = window.__header_sale_data__;
  }

  if (!innerSaleData) {
    return links;
  }

  if (innerSaleData.secondaryUrl) {
    links.push({
      url: innerSaleData.secondaryUrl,
      text: SECONDARY_LINK_TITLE,
      analyticsLabel: 'Flat_old_sale',
    });
  }

  if (innerSaleData.jksSaleUrl) {
    links.push({
      url: innerSaleData.jksSaleUrl,
      text: JK_FLAT_LINK_TITLE,
      analyticsLabel: 'JK_sale',
    });
  }

  if (innerSaleData.kpUrl) {
    links.push({
      url: innerSaleData.kpUrl,
      text: COTTAGE_LINK_TITLE,
      analyticsLabel: 'KP_sale',
    });
  }

  if (innerSaleData.builderLandingUrl) {
    links.push({
      url: innerSaleData.builderLandingUrl,
      text: BUILDER_PROMO_LINK_TITLE,
      isCountrysidePromoLink: true,
      analyticsLabel: 'build_house',
      target: '_blank',
    });
  }

  return links;
};

export const getInitialNewBuildingLinks = (newbuildingsData?: IHeaderNewbuildingsData) => {
  let innerNewbuildingsData = newbuildingsData;
  const links: IDropdownMenuLink[] = [];

  if (typeof window !== 'undefined') {
    innerNewbuildingsData = window.__header_newbuildings_data__;
  }

  if (!innerNewbuildingsData) {
    return links;
  }

  if (innerNewbuildingsData.jksNewobjectsUrl) {
    links.push({
      url: innerNewbuildingsData.jksNewobjectsUrl,
      text: JKS_LINK_TITLE,
      analyticsLabel: 'JK_new',
    });
  }

  if (innerNewbuildingsData.villagesUrl) {
    links.push({
      url: innerNewbuildingsData.villagesUrl,
      text: KP_LINK_TITLE,
      analyticsLabel: 'KP_new',
    });
  }

  return links;
};

export const getInitialMortgageLinks = (baseHost: string = '//www.cian.ru/') => {
  return [
    {
      text: 'Ипотечная анкета',
      url: mergePaths('//www.cian.ru/', '/ipoteka-main?banner=header'),
    },
    {
      text: 'Ипотека',
      url: mergePaths(baseHost, '/ipoteka/?banner=header'),
    },
    {
      text: 'Ипотечный калькулятор',
      url: mergePaths(baseHost, '/ipoteka/calculator?banner=header-calc'),
    },
  ];
};

export const dropdownLinks: ILinks = {
  rent: getInitialRentLinks(),
  sale: getInitialSaleLinks(),
  newbuildings: getInitialNewBuildingLinks(),
  commercial: getInitialCommercialLinks(),
  mortgage: getInitialMortgageLinks(),
};

export const updateCommercialLinks = (data: IHeaderV1Schema) => {
  return dropdownLinks.commercial.reduce((acc, link) => {
    switch (link.text) {
      case BC_LINK_TITLE:
        link.url = data.bcUrl || link.url;

        return data.isBcRegion ? acc.concat(link) : acc;
      case SC_LINK_TITLE:
        link.url = data.scUrl || link.url;

        return data.isScRegion ? acc.concat(link) : acc;
      case WC_LINK_TITLE:
        link.url = data.wcUrl || link.url;

        return data.isWcRegion ? acc.concat(link) : acc;
      default:
        return acc;
    }
  }, [] as IDropdownMenuLink[]);
};

export const updateSaleLinks = (data: IHeaderV1Schema) => {
  return dropdownLinks.sale.reduce((acc, link) => {
    switch (link.text) {
      case SECONDARY_LINK_TITLE:
        link.url = data.secondaryUrl || link.url;

        return acc.concat(link);
      case JK_FLAT_LINK_TITLE:
        link.url = data.jksSaleUrl || link.url;

        return acc.concat(link);
      case COTTAGE_LINK_TITLE:
        link.url = data.kpUrl || link.url;

        return acc.concat(link);
      default:
        return acc;
    }
  }, [] as IDropdownMenuLink[]);
};

export const updateNewbuildingsLinks = (data: IHeaderV1Schema) => {
  return dropdownLinks.newbuildings.reduce((acc, link) => {
    switch (link.text) {
      case JKS_LINK_TITLE:
        link.url = data.jksNewobjectsUrl || link.url;

        return acc.concat(link);
      case KP_LINK_TITLE:
        link.url = data.villagesUrl || link.url;

        return acc.concat(link);
      default:
        return acc;
    }
  }, [] as IDropdownMenuLink[]);
};

export const dropdownUrlChangeHandler = (data: IHeaderV1Schema) => {
  dropdownLinks.sale = updateSaleLinks(data);
  dropdownLinks.newbuildings = updateNewbuildingsLinks(data);
  dropdownLinks.commercial = updateCommercialLinks(data);
};

export const linksExist = (name: TDropdownType) => {
  return dropdownLinks[name].length > 0;
};
